import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Side safe area styles for scan to pay modals on compact screens: the container keeps content
 * clear of side insets, such as the iPhone Duo system rail, and the content gets a light border
 * along each inset edge.
 */
export const useScanToPayModalHorizontalSafeArea = () => {
  const { currentBreakpoint } = useResponsive();
  const { right: safeAreaRight, left: safeAreaLeft } = useSafeAreaInsets();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const hasRightSafeArea = safeAreaRight > 0;
  const hasLeftSafeArea = safeAreaLeft > 0;
  const shouldApplySafeArea = currentBreakpoint.isXS && (hasRightSafeArea || hasLeftSafeArea);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyle = useStyle(() => {
    if (!shouldApplySafeArea) {
      return;
    }

    return { paddingRight: safeAreaRight, paddingLeft: safeAreaLeft };
  }, [safeAreaLeft, safeAreaRight, shouldApplySafeArea]);

  const contentStyles = [
    shouldApplySafeArea && hasRightSafeArea ? styles.contentWithBorderRight : undefined,
    shouldApplySafeArea && hasLeftSafeArea ? styles.contentWithBorderLeft : undefined,
  ];

  return { containerStyle, contentStyles };
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentWithBorderRight: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.LIGHT,
  },
  contentWithBorderLeft: {
    borderLeftWidth: 1,
    borderLeftColor: theme.colors.LIGHT,
  },
});
